import React, { useEffect, useState } from "react";
import { headshotUrl, teamColor, teamKey, teamLogoUrl, teamTextColor } from "@/lib/teams";
import { http } from "@/lib/api";

// Sprint 68 — LED / 3D team badge: glowing team-color ring, glossy dome, ESPN logo.
export const TeamLogo = ({ team, size = 28, className = "", withGlow = true }) => {
  const url = teamLogoUrl(team);
  const color = teamColor(team);
  if (!url) return null;
  return (
    <span
      data-testid={`team-logo-${teamKey(team)}`}
      className={`team-led inline-flex items-center justify-center rounded-full shrink-0 ${className}`}
      style={{
        width: size, height: size, "--led": color,
        boxShadow: withGlow
          ? `0 0 ${Math.round(size * 0.45)}px ${color}99, 0 0 2px ${color}, inset 0 ${Math.round(size * 0.12)}px ${Math.round(size * 0.25)}px rgba(255,255,255,0.28), inset 0 -${Math.round(size * 0.12)}px ${Math.round(size * 0.25)}px rgba(0,0,0,0.65)`
          : `inset 0 2px 4px rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.6)`,
        background: `radial-gradient(circle at 35% 30%, ${color}66 0%, #0a0a0a 70%)`,
        border: `1px solid ${color}`,
      }}
      title={teamKey(team)}
    >
      <img src={url} alt={teamKey(team)} loading="lazy" draggable={false}
           style={{ width: size * 0.72, height: size * 0.72, objectFit: "contain", filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.8))" }} />
    </span>
  );
};

// Team abbreviation in the team's primary color (lifted for dark palettes) + optional LED logo.
export const TeamTag = ({ team, size = 22, logo = true, className = "", textClass = "" }) => (
  <span className={`inline-flex items-center gap-1.5 ${className}`}>
    {logo && <TeamLogo team={team} size={size} />}
    <span className={`mono font-black ${textClass}`} style={{ color: teamTextColor(team), textShadow: `0 0 8px ${teamColor(team)}66` }}>
      {teamKey(team) || "—"}
    </span>
  </span>
);

// dk_id → sleeper_id map for the loaded pool (one fetch per page load, shared).
let _headshotMap = null;
let _headshotPromise = null;
export const loadHeadshotMap = () => {
  if (_headshotMap) return Promise.resolve(_headshotMap);
  if (!_headshotPromise) {
    _headshotPromise = http.get("/dk/headshots").then((r) => { _headshotMap = r.data?.map || {}; return _headshotMap; })
      .catch(() => { _headshotMap = {}; return _headshotMap; });
  }
  return _headshotPromise;
};
export const resetHeadshotMap = () => { _headshotMap = null; _headshotPromise = null; };
if (typeof window !== "undefined") window.addEventListener("wp:pool-refreshed", resetHeadshotMap);

export const useHeadshotMap = () => {
  const [map, setMap] = useState(_headshotMap || {});
  useEffect(() => { loadHeadshotMap().then(setMap); }, []);
  return map;
};

// Player headshot with team-color LED ring; DST rows show the team logo; silhouette fallback.
export const PlayerHeadshot = ({ player, sleeperId, size = 30, className = "" }) => {
  const [broken, setBroken] = useState(false);
  const team = player?.team;
  const isDst = String(player?.position || "").toUpperCase() === "DST";
  const url = !isDst && !broken ? headshotUrl(sleeperId || player?.sleeper_id) : null;
  if (isDst) return <TeamLogo team={team} size={size} className={className} />;
  const color = teamColor(team);
  return (
    <span
      data-testid={`headshot-${player?.dk_id}`}
      className={`inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 bg-[#111] ${className}`}
      style={{ width: size, height: size, border: `1.5px solid ${color}`, boxShadow: `0 0 8px ${color}66, inset 0 -3px 6px rgba(0,0,0,0.6)` }}
    >
      {url ? (
        <img src={url} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)}
             style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
      ) : (
        <svg viewBox="0 0 24 24" width={size * 0.7} height={size * 0.7} fill="#52525b" aria-hidden="true">
          <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8z" />
        </svg>
      )}
    </span>
  );
};
